import { BIDDERS_CRUD_ACTION_CREATORS } from '../actions/bidders_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import { getBackendUri } from '../utils/EnvConfigUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { APPROVAL_RULES_CRUD_ACTION_CREATORS } from '../actions/company_approval_actions';
import { message } from 'antd';
import { logoutSuccess } from '../actions/session_actions';
import { nullSafeGet } from '../utils/DataAccessUtils';
import {
	fetchAllMessagesError,
	fetchAllMessagesStart,
	fetchAllMessagesSuccess,
} from '../actions/messages_actions';
import { normalizeNotesResponse } from './messages_thunks';
import BiddersPipelineFilters from '../components/requests_for_proposal_index_page/BiddersPipelineFilters';

const BASE_URL = getBackendUri();
const fetchUtils = new FetchUtils();

export const biddersRestCrudThunksForBuyer = new RestCrudThunks(
	'bidders',
	'bidder',
	'bidder',
	'bidders',
	BIDDERS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'quote/'
);
export const biddersRestCrudThunksForSupplier = new RestCrudThunks(
	'bidders',
	'bidder',
	'bidder',
	'bidders',
	BIDDERS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'quote/'
);

export const changeBidderAwardStatus = (roleType) => (rfpId, supplierFacilityId, status) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/quote/bidders`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.patch(
					baseUrl + apiUrl + `/${rfpId}/${supplierFacilityId}/${status}`,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					})
				)
				.then((response: any) => response.json())
				.then((d) => {
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const changeBidderAwardStatusForBuyer = changeBidderAwardStatus(ROLE_TYPES.BUYER);
export const changeBidderAwardStatusForSupplier = changeBidderAwardStatus(ROLE_TYPES.SUPPLIER);

export const readBidders =
	(roleType) =>
	(
		rpfId,
		params = {},
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null
	) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/quote/bidders`;
		// if pagination present, transform into limit/offset format and add to query params
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(BIDDERS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));
				fetchUtils
					.get(
						baseUrl + apiUrl + `?requestForProposalId=${rpfId}`,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then((response: any) => response.json())
					.then((d) => {
						if (d.status === 'error') {
							dispatch(BIDDERS_CRUD_ACTION_CREATORS.fetchError(d.message, targetCollectionName));
							reject(d.message);
						} else {
							dispatch(
								BIDDERS_CRUD_ACTION_CREATORS.fetchSuccess(
									d.data.map((el) => ({
										...el,
									})),
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters
								)
							);
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(BIDDERS_CRUD_ACTION_CREATORS.fetchError(d.message, targetCollectionName));
						reject(d.message);
					});
			});
		};
	};

export const readBiddersForBuyer = readBidders(ROLE_TYPES.BUYER);
export const readBiddersForSupplier = readBidders(ROLE_TYPES.SUPPLIER);

export const remindBiddersToMakeProposal = (userRole) => (id, supplierFacilityId) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(APPROVAL_RULES_CRUD_ACTION_CREATORS.fetchStart());

			fetchUtils
				.post(
					BASE_URL +
						`/api/v1/${userRole}/quote/requests_for_proposal/${id}/supplier_facility_reminder/${supplierFacilityId}`,
					{}
				)
				.then((response: any) => response.json())
				.then((d) => {
					if (d.status === 'error') {
						dispatch(APPROVAL_RULES_CRUD_ACTION_CREATORS.fetchError([d.message], ''));
						message.error('Reminder Failed.');
						reject(d.message);
					} else {
						message.success('Reminder Sent.');
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(APPROVAL_RULES_CRUD_ACTION_CREATORS.fetchError([d.message], ''));
					reject(d.message);
				});
		});
	};
};

// Shared by the singular and bulk append endpoints — both return the same flat note
// array and refresh the same message channel.
const appendBidderNotesRequest = (apiUrl, bidder, entity) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(BIDDERS_CRUD_ACTION_CREATORS.updateStart(entity));

			fetchUtils
				.patch(apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: entity,
				})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(BIDDERS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
						reject(d.message);
					} else {
						const channelId = `${nullSafeGet('requestForProposalId', bidder)}_${nullSafeGet(
							'supplierFacilityId',
							bidder
						)}`;
						// Append response shape: d.data is a flat array of notes (same as fetch).
						dispatch(fetchAllMessagesSuccess(channelId, normalizeNotesResponse(d.data)));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(BIDDERS_CRUD_ACTION_CREATORS.createError(d.message, entity));
					reject(d.message);
				});
		});
	};
};

const bidderNotesUrl = (roleType, bidder) =>
	`${getBackendUri()}/api/v1/${roleType}/quote/requests_for_proposal/${nullSafeGet(
		'requestForProposalId',
		bidder
	)}/supplier/${nullSafeGet('supplierFacilityId', bidder)}/append_notes`;

// entity must include rfpId and a note field
export const appendBidderNotes = (roleType) => (bidder, entity) =>
	appendBidderNotesRequest(bidderNotesUrl(roleType, bidder), bidder, entity);

// entity must include rfpId and a notes array. Photo-only server-side, and it publishes a
// single grouped note notification for the whole batch instead of one per note.
export const appendBidderNotesBulk = (roleType) => (bidder, entity) =>
	appendBidderNotesRequest(`${bidderNotesUrl(roleType, bidder)}/bulk`, bidder, entity);

export const fetchAllBidderNotes = (roleType) => (bidder) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			const id = `${bidder.requestForProposalId}_${bidder.supplierFacilityId}`;
			dispatch(fetchAllMessagesStart(id));

			fetchUtils
				.get(
					BASE_URL +
						`/api/v1/${roleType}/quote/requests_for_proposal/${bidder.requestForProposalId}/supplier/${bidder.supplierFacilityId}/notes`,
					{
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					}
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(fetchAllMessagesError(d.message, id));
						reject(d.message);
					} else {
						dispatch(fetchAllMessagesSuccess(id, normalizeNotesResponse(d.data)));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(fetchAllMessagesError(d.message, id));
					reject(d.message);
				});
		});
	};
};

export const refreshBiddersCount = (userType) => (filters) => (dispatch) => {
	const apiUrl = `/api/v1/${userType}/quote/bidders/count_by_status`;

	const data = {
		...(filters || {}),
		statuses: BiddersPipelineFilters.map((_) => _.name).join(','),
		status: undefined,
	};
	fetchUtils
		.get(
			BASE_URL + apiUrl,
			Object.assign(
				{
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				},
				data
			)
		)
		.then(parseJSON)
		.then(({ json, status, ok }) => {
			const d = json;
			if (d.status === 'error') {
				message.error(d.message);
			} else {
				if (d.data && d.data.length > 0) {
					d.data.map((category) =>
						dispatch(
							BIDDERS_CRUD_ACTION_CREATORS.countBySuccess(category.count, category.categoryName)
						)
					);
				}
			}
		})
		.catch((d) => {
			message.error(d.message);
		});
};

export const readBidderForSupplier = (rpfId, supplierFacilityId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/supplier/quote/bidders/${rpfId}/${supplierFacilityId}`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(
					baseUrl + apiUrl + `?requestForProposalId=${rpfId}`,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					})
				)
				.then((response: any) => response.json())
				.then((d) => {
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};
